'use client'

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react'
import type { ToastProps, ToastType } from './Toast'

type ToastTypeWithId = Omit<ToastProps, 'onClose'> & { id: string }

type ToastContextType = {
  showToast: (toast: Omit<ToastProps, 'onClose'>) => void
  currentToast: ToastTypeWithId | null
  removeToast: () => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentToast, setCurrentToast] = useState<ToastTypeWithId | null>(null)

  const showToast = useCallback((toast: Omit<ToastProps, 'onClose'>) => {
    const id = Math.random().toString(36).substring(2, 9)
    setCurrentToast({ ...toast, id })
  }, [])

  const removeToast = useCallback(() => {
    setCurrentToast(null)
  }, [])

  useEffect(() => {
    if (currentToast) {
      const timer = setTimeout(() => {
        removeToast()
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [currentToast, removeToast])

  return (
    <ToastContext.Provider value={{ showToast, currentToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  )
}

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
